/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "../../utils";

const buttonVariants = cva(
  ("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 active:translate-y-px" +
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none" +
    "focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-[3px] focus-visible:shadow-ring-glow aria-invalid:ring-destructive" +
    "aria-invalid:border-destructive"),
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-elevation1 hover:opacity-95 hover:shadow-elevation2",
        primary: "bg-primary text-primary-foreground shadow-elevation1 hover:opacity-95 hover:shadow-elevation2",
        destructive: "bg-destructive text-destructive-foreground shadow-elevation1 hover:opacity-95 focus-visible:ring-destructive dark:bg-destructive",
        danger: "bg-destructive text-destructive-foreground shadow-elevation1 hover:opacity-95 focus-visible:ring-destructive dark:bg-destructive",
        outline: "surface-control border text-foreground hover:border-ring/60 hover:text-foreground",
        secondary: "surface-panel border text-secondary-foreground hover:border-ring/50 hover:text-foreground",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        softDanger: "surface-control border border-destructive/35 text-destructive hover:border-destructive/60 hover:bg-destructive/10 focus-visible:ring-destructive",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const actionSurfaceVariants = cva(
  "group relative rounded-lg border text-left transition-[background-color,border-color,box-shadow,transform] outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      role: {
        optionRow: "surface-card flex w-full flex-col items-start gap-1.5 px-3 py-2.5 hover:border-ring/50",
        metricCard: "surface-card flex w-full flex-col items-start justify-between gap-1 px-3 py-3",
      },
      tone: {
        default: "",
        pending: "border-amber-500/30 bg-amber-500/[0.06] text-amber-700 dark:text-amber-200 data-[active=true]:border-amber-500/50 data-[active=true]:bg-amber-500/[0.12]",
        inProgress: "border-violet-500/30 bg-violet-500/[0.06] text-violet-700 dark:text-violet-200 data-[active=true]:border-violet-500/50 data-[active=true]:bg-violet-500/[0.12]",
        atRisk: "border-orange-500 bg-orange-500/[0.06] text-orange-700 dark:text-orange-200 data-[active=true]:bg-orange-500/[0.12]",
        overdue: "border-destructive surface-card data-[active=true]:bg-muted",
        /** @deprecated Use `overdue` for deadline breaches; kept for legacy call sites. */
        danger: "border-destructive surface-card data-[active=true]:bg-muted",
        success: "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-200 data-[active=true]:border-emerald-500/45 data-[active=true]:bg-emerald-500/[0.12]",
      },
        active: {
        true: "border-primary bg-accent text-accent-foreground shadow-elevation1",
        false: "text-foreground",
      },
    },
    defaultVariants: {
      role: "optionRow",
      tone: "default",
      active: false,
    },
  },
);

interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, isLoading = false, disabled, children, type, ...props },
  ref,
) {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ref,
      className: cn(buttonVariants({ variant, size, className }), (children.props as { className?: string }).className),
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={[disabled === true, isLoading].includes(true)}
      type={type ?? "button"}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
});

export { Button, actionSurfaceVariants, buttonVariants };
